import { BRAND } from '../brand.js';
import { ORDER_TYPES } from '../constants/pos.js';
import { formatInZone } from '../time/index.js';
import { CODE_PAGES } from './codepages.js';

/**
 * Print templates: view-models → print documents (see layout.js for the block types).
 * Receipts and credit notes take the shared receipt model (billing/receipt.js), so thermal,
 * browser and e-receipt copies always say the same thing.
 */

const orderTypeLabel = (k) => ORDER_TYPES.find((o) => o.key === k)?.label ?? k ?? '';

function header(model, { cpl, showLogo }) {
  const b = [];
  if (showLogo && model.business?.logoUrl) b.push({ t: 'image', name: 'logo' });
  const name = model.business?.name ?? '';
  const big = [...name].length <= Math.floor(cpl / 2);
  b.push({ t: 'text', text: name, align: 'center', bold: true, w: big ? 2 : 1, h: 2 });
  if (model.business?.legalName && model.business.legalName !== name)
    b.push({ t: 'text', text: model.business.legalName, align: 'center' });
  if (model.outlet?.name) b.push({ t: 'text', text: model.outlet.name, align: 'center' });
  for (const l of model.outlet?.addressLines ?? []) b.push({ t: 'text', text: l, align: 'center' });
  if (model.outlet?.phone) b.push({ t: 'text', text: `Ph: ${model.outlet.phone}`, align: 'center' });
  if (model.gstin) b.push({ t: 'text', text: `GSTIN: ${model.gstin}`, align: 'center' });
  if (model.fssai) b.push({ t: 'text', text: `FSSAI: ${model.fssai}`, align: 'center' });
  for (const l of model.headerLines ?? []) b.push({ t: 'text', text: l, align: 'center' });
  return b;
}

function itemRows(model, { wide, large }) {
  const h = large ? 2 : 1;
  const b = [
    {
      t: 'cols',
      bold: true,
      cols: wide
        ? [
            { text: 'Item', w: '*' },
            { text: 'Qty', w: 4, align: 'right' },
            { text: 'Rate', w: 9, align: 'right' },
            { text: 'Amount', w: 10, align: 'right' },
          ]
        : [
            { text: 'Item', w: '*' },
            { text: 'Qty', w: 3, align: 'right' },
            { text: 'Amount', w: 9, align: 'right' },
          ],
    },
    { t: 'rule' },
  ];
  for (const l of model.lines ?? []) {
    b.push({
      t: 'cols',
      h,
      cols: wide
        ? [
            { text: l.name, w: '*' },
            { text: String(l.qty), w: 4, align: 'right' },
            { text: l.rate, w: 9, align: 'right' },
            { text: l.amount, w: 10, align: 'right' },
          ]
        : [
            { text: l.name, w: '*' },
            { text: String(l.qty), w: 3, align: 'right' },
            { text: l.amount, w: 9, align: 'right' },
          ],
    });
    for (const m of l.modifiers ?? []) b.push({ t: 'text', text: `  + ${m}` });
    if (l.note) b.push({ t: 'text', text: `  Note: ${l.note}` });
  }
  return b;
}

function taxTable(rows, { wide }) {
  if (!rows?.length) return [];
  const igst = rows.some((r) => r.igst);
  const cess = wide && rows.some((r) => r.cess);
  const cols = (r) =>
    [
      { text: r.rate, w: wide ? 8 : 6 },
      { text: r.taxable, w: '*', align: 'right' },
      ...(igst ? [{ text: r.igst ?? '', w: 9, align: 'right' }] : []),
      ...(!igst ? [{ text: r.cgst ?? '', w: wide ? 9 : 7, align: 'right' }] : []),
      ...(!igst ? [{ text: r.sgst ?? '', w: wide ? 9 : 7, align: 'right' }] : []),
      ...(cess ? [{ text: r.cess ?? '', w: 8, align: 'right' }] : []),
    ].filter(Boolean);
  return [
    { t: 'rule' },
    {
      t: 'cols',
      bold: true,
      cols: cols({ rate: 'GST', taxable: 'Taxable', igst: 'IGST', cgst: 'CGST', sgst: 'SGST', cess: 'Cess' }),
    },
    ...rows.map((r) => ({ t: 'cols', cols: cols(r) })),
  ];
}

/** Customer receipt / tax invoice / pre-bill / credit note. */
export function receiptDocument(model, { profile }) {
  const cpl = profile.charsPerLine;
  const wide = cpl >= 42;
  const opts = model.options ?? {};
  const large = opts.fontSize === 'large';
  const b = [...header(model, { cpl, showLogo: opts.showLogo !== false && profile.printLogo !== false })];

  b.push({ t: 'rule' }, { t: 'text', text: model.title, align: 'center', bold: true });
  if (model.watermark)
    b.push({ t: 'text', text: ` *** ${model.watermark} *** `, align: 'center', bold: true, invert: true });
  if (model.copyLabel) b.push({ t: 'text', text: ` ${model.copyLabel} `, align: 'center', bold: true, invert: true });
  b.push({ t: 'rule' });
  for (const m of model.meta ?? []) b.push({ t: 'pair', left: `${m.label}:`, right: m.value });
  b.push({ t: 'rule' }, ...itemRows(model, { wide, large }), { t: 'rule' });
  for (const r of model.totals ?? []) b.push({ t: 'pair', left: r.label, right: r.value });
  b.push(
    { t: 'rule', ch: '=' },
    { t: 'pair', left: model.grandTotalLabel ?? 'TOTAL', right: `₹${model.grandTotal}`, bold: true, h: 2 },
    model.taxNote && { t: 'text', text: `(${model.taxNote})`, align: 'center' },
    { t: 'rule', ch: '=' },
  );
  for (const p of model.payments ?? []) {
    b.push({ t: 'pair', left: p.label, right: p.amount });
    if (p.tendered) b.push({ t: 'pair', left: '  Tendered', right: p.tendered });
    if (p.change) b.push({ t: 'pair', left: '  Change', right: p.change });
  }
  if (model.due) b.push({ t: 'pair', left: 'Balance due', right: `₹${model.due}`, bold: true });
  b.push(...taxTable(model.taxBreakup, { wide }));
  if (model.upiUri) {
    b.push(
      { t: 'rule' },
      { t: 'text', text: 'Scan to pay with any UPI app', align: 'center', bold: true },
      { t: 'qr', data: model.upiUri, size: 'medium' },
    );
  }
  if (opts.showLinkQr && model.receiptUrl) {
    b.push(
      { t: 'text', text: 'Scan for your e-receipt', align: 'center' },
      { t: 'qr', data: model.receiptUrl, size: 'small' },
    );
  }
  if (model.footerLines?.length) {
    b.push({ t: 'blank' });
    for (const l of model.footerLines) b.push({ t: 'text', text: l, align: 'center' });
  }
  if (model.poweredBy !== false) b.push({ t: 'text', text: BRAND.poweredByText, align: 'center' });
  return { blocks: b };
}

/** Kitchen order ticket: big item lines, no prices. */
export function kotDocument(kot, { outletName, timezone = 'Asia/Kolkata', large = true, profile }) {
  const cpl = profile.charsPerLine;
  const h = large ? 2 : 1;
  const cancel = kot.type === 'cancel';
  const b = [
    { t: 'text', text: cancel ? 'CANCEL KOT' : 'KOT', align: 'center', bold: true, w: 2, h: 2, invert: cancel },
    {
      t: 'text',
      text: [outletName, String(kot.station ?? '').toUpperCase()].filter(Boolean).join(' - '),
      align: 'center',
    },
    { t: 'rule' },
    {
      t: 'pair',
      left: `KOT #${kot.kotNo}`,
      right: formatInZone(kot.createdAt ?? new Date(), timezone, 'hh:mm a'),
      bold: true,
    },
  ];
  if (kot.tokenNo != null) b.push({ t: 'text', text: `TOKEN ${kot.tokenNo}`, bold: true, w: 2, h: 2 });
  if (kot.tableNo) b.push({ t: 'text', text: `TABLE ${kot.tableNo}`, bold: true, h: 2 });
  b.push({ t: 'text', text: orderTypeLabel(kot.orderType).toUpperCase(), bold: true }, { t: 'rule' });
  for (const l of kot.lines ?? []) {
    const name = l.variantName ? `${l.name} (${l.variantName})` : l.name;
    b.push({
      t: 'cols',
      bold: true,
      h,
      cols: [
        { text: `${cancel ? '-' : ''}${l.qty}`, w: 4 },
        { text: name, w: '*' },
      ],
    });
    for (const m of l.modifiers ?? []) b.push({ t: 'text', text: `     + ${m}` });
    if (l.note) b.push({ t: 'text', text: `     NOTE: ${l.note}`, bold: true });
  }
  b.push({ t: 'rule' });
  if (kot.createdBy) b.push({ t: 'text', text: `By ${kot.createdBy}`, align: cpl >= 42 ? 'right' : 'left' });
  return { blocks: b };
}

/** Token slip for counter-service outlets: customers wait for their number to be called. */
export function tokenSlipDocument(t, { profile }) {
  const big = profile.charsPerLine >= 42 ? 4 : 3;
  const b = [
    { t: 'text', text: t.businessName, align: 'center', bold: true },
    t.outletName && { t: 'text', text: t.outletName, align: 'center' },
    { t: 'rule' },
    { t: 'text', text: 'YOUR TOKEN', align: 'center', bold: true },
    { t: 'text', text: String(t.tokenNo ?? '-'), align: 'center', bold: true, w: big, h: big },
    { t: 'rule' },
    { t: 'pair', left: orderTypeLabel(t.orderType), right: `${t.itemCount} item${t.itemCount === 1 ? '' : 's'}` },
    t.invoiceNo && { t: 'pair', left: 'Bill', right: t.invoiceNo },
    {
      t: 'pair',
      left: 'Time',
      right: formatInZone(t.date ?? new Date(), t.timezone ?? 'Asia/Kolkata', 'dd/MM/yyyy hh:mm a'),
    },
  ];
  if (t.footer) b.push({ t: 'blank' }, { t: 'text', text: t.footer, align: 'center' });
  return { blocks: b.filter(Boolean) };
}

/** Test page: profile summary, ruler, every text style, code page sample, QR and barcode. */
export function testPageDocument({
  printerName,
  connectionLabel,
  profile,
  timezone = 'Asia/Kolkata',
  now = new Date(),
}) {
  const cpl = profile.charsPerLine;
  const ruler = Array.from({ length: cpl }, (_, i) => String((i + 1) % 10)).join('');
  return {
    blocks: [
      { t: 'text', text: 'TEST PAGE', align: 'center', bold: true, w: 2, h: 2 },
      { t: 'text', text: BRAND.printAgentName.replace(' Print Agent', ''), align: 'center' },
      { t: 'rule' },
      { t: 'pair', left: 'Printer', right: printerName ?? '-' },
      connectionLabel && { t: 'pair', left: 'Connection', right: connectionLabel },
      { t: 'pair', left: 'Paper', right: `${profile.paperWidth} / ${cpl} chars / ${profile.dotWidth} dots` },
      { t: 'pair', left: 'Code page', right: CODE_PAGES[profile.codePage]?.label ?? profile.codePage },
      { t: 'pair', left: 'Printed', right: formatInZone(now, timezone, 'dd/MM/yyyy hh:mm:ss a') },
      { t: 'rule' },
      { t: 'text', text: ruler },
      { t: 'text', text: 'Left aligned' },
      { t: 'text', text: 'Centered', align: 'center' },
      { t: 'text', text: 'Right aligned', align: 'right' },
      { t: 'text', text: 'Bold text', bold: true },
      { t: 'text', text: 'Underlined text', underline: true },
      { t: 'text', text: ' Inverted text ', invert: true },
      { t: 'text', text: 'Double height', h: 2 },
      { t: 'text', text: 'Double size', w: 2, h: 2 },
      { t: 'pair', left: 'Column left', right: '1,234.50' },
      { t: 'rule' },
      { t: 'text', text: 'Rupee: ₹1,234.50' },
      { t: 'text', text: 'Telugu: తెలుగు   Hindi: हिन्दी' },
      {
        t: 'text',
        text: profile.rasterText
          ? '(regional text above is printed as an image)'
          : '(turn on "image text" in printer settings to print these)',
      },
      { t: 'rule' },
      { t: 'text', text: 'QR code', align: 'center' },
      { t: 'qr', data: 'https://example.com/shripos-test', size: 'small' },
      { t: 'text', text: 'Barcode', align: 'center' },
      { t: 'barcode', data: '8901058000108' },
      { t: 'text', text: 'If the QR code is missing, set QR mode to "Image".', align: 'center' },
    ].filter(Boolean),
  };
}
